import { DateTime } from 'luxon';

export const REPLAY_AVAILABLE_DELAY_MINUTES = 15;

export type AttendeeLiveDecision =
  | { kind: 'wait' }
  | { kind: 'unavailable' }
  | { kind: 'redirect'; to: 'waiting-room' | 'thank-you' }
  | { kind: 'live' }
  | { kind: 'replay' }
  | { kind: 'finished' };

export interface AttendeeLiveRouteInput {
  /**
   * Faux tant que le statut n'a pas été confirmé par un chargement abouti sur la page en cours.
   * Une valeur laissée en mémoire par un passage précédent ne suffit pas : c'est elle qui faisait
   * repartir le spectateur vers la salle d'attente au moment de la bascule.
   */
  statusConfirmed: boolean;
  /** Le chargement s'est soldé par une erreur : état terminal, jamais un écran d'attente sans fin. */
  statusUnavailable: boolean;
  status?: string;
  token: string | null;
  finishedAt: string | null;
  replayEnabled: boolean;
  youtubeClosePending: boolean;
  nowIso: string;
}

function minutesSince(finishedAt: string | null, nowIso: string): number | null {
  if (!finishedAt) return null;
  const end = DateTime.fromISO(finishedAt, { zone: 'utc' });
  const now = DateTime.fromISO(nowIso, { zone: 'utc' });
  if (!end.isValid || !now.isValid) return null;
  return now.diff(end, 'minutes').minutes;
}

export function decideAttendeeLiveRoute(input: AttendeeLiveRouteInput): AttendeeLiveDecision {
  const {
    statusConfirmed,
    statusUnavailable,
    status,
    token,
    finishedAt,
    replayEnabled,
    youtubeClosePending,
    nowIso,
  } = input;

  if (!statusConfirmed) {
    return statusUnavailable ? { kind: 'unavailable' } : { kind: 'wait' };
  }

  if (status === 'UPCOMING' && token) {
    return { kind: 'redirect', to: 'waiting-room' };
  }

  if (status === 'FINISHED') {
    if (youtubeClosePending) {
      return { kind: 'live' };
    }

    const elapsed = minutesSince(finishedAt, nowIso);
    const replayDelayReached = elapsed != null && elapsed >= REPLAY_AVAILABLE_DELAY_MINUTES;

    if (replayDelayReached) {
      return replayEnabled ? { kind: 'replay' } : { kind: 'finished' };
    }

    if (token) {
      return { kind: 'redirect', to: 'thank-you' };
    }
  }

  return { kind: 'live' };
}
